import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import { FaRegCreditCard, FaTimesCircle } from "react-icons/fa";
import {
  createStripePaymentIntent,
  persistStripePaymentRecordIdFromIntentResponse,
  syncStripePaymentToBackend,
} from "./stripePaymentBackend";

const receiptDetails = {
  bankName: "LLOYDS",
  accountName: "Sundarban cargo services uk limited",
  accountNumber: "75834260",
  sort_code: "30-99-50",
  status: "Bank Details",
};

export default function PaymentFailed() {
  const location = useLocation();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const query = new URLSearchParams(location.search);

    const run = async () => {
      const statePi = location.state?.paymentIntent;
      if (statePi && !cancelled) {
        await syncStripePaymentToBackend(statePi, { paymentStatusId: 3 });
        return;
      }

      const clientSecret =
        query.get("payment_intent_client_secret") || query.get("setup_intent_client_secret");
      const pk = sessionStorage.getItem("stripePublishableKey");
      const piIdFallback = query.get("payment_intent") || "";

      if (!pk || !clientSecret) {
        if (piIdFallback && !cancelled) {
          await syncStripePaymentToBackend(null, { paymentIntentId: piIdFallback, paymentStatusId: 3 });
        }
        return;
      }

      try {
        const stripe = await loadStripe(pk);
        if (!stripe || cancelled) return;
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        if (cancelled) return;
        await syncStripePaymentToBackend(paymentIntent, { paymentIntentId: piIdFallback, paymentStatusId: 3 });
      } catch {
        if (piIdFallback && !cancelled) {
          await syncStripePaymentToBackend(null, { paymentIntentId: piIdFallback, paymentStatusId: 3 });
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [location.search, location.state]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-rose-50 via-white to-orange-50 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 30%, rgba(244, 63, 94, 0.2) 0%, transparent 45%), radial-gradient(circle at 85% 70%, rgba(251, 146, 60, 0.18) 0%, transparent 50%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="rounded-3xl bg-white/90 backdrop-blur-sm shadow-2xl shadow-rose-900/10 border border-rose-100/80 p-8 sm:p-10 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 shadow-lg shadow-rose-500/35">
            <FaTimesCircle className="h-10 w-10 text-white" aria-hidden />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-600/90 mb-2">
            Payment not completed
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Something went wrong
          </h1>
          <p className="mt-4 text-gray-600 leading-relaxed">
            We couldn’t charge your card. No worries—your booking details are safe. Try again with
            another card or contact support if the problem continues.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-xl bg-rose-50/80 border border-rose-100 px-4 py-3 text-sm text-rose-900/80">
            <FaRegCreditCard className="shrink-0 text-rose-500" aria-hidden />
            <span>Check your card details, limit, and 3D Secure approval.</span>
          </div>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 text-left">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">{receiptDetails.status}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Pay later
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs font-semibold text-gray-500">Bank name</div>
                <div className="text-sm font-medium text-gray-900">{receiptDetails.bankName}</div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs font-semibold text-gray-500">Account name</div>
                <div className="text-sm font-medium text-gray-900 text-right">
                  {receiptDetails.accountName}
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs font-semibold text-gray-500">Account number</div>
                <div className="text-sm font-medium text-gray-900">{receiptDetails.accountNumber}</div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs font-semibold text-gray-500">Sort code</div>
                <div className="text-sm font-medium text-gray-900">{receiptDetails.sort_code}</div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-sm text-red-600 max-w-md mx-auto">
            <strong>Important:</strong> After finishing the booking, a <strong>Payment Slip</strong>{" "}
            will open in a new tab.
            <br />
            Please get the <strong>Parcel Number</strong> from the slip and use it as the{" "}
            <strong>payment reference</strong>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              disabled={retrying}
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5"
              onClick={async () => {
                try {
                  setRetrying(true);

                  const raw = localStorage.getItem("paymentSlipData");
                  const slip = raw ? JSON.parse(raw) : null;
                  const parcelId = slip?.parcelId ? Number(slip.parcelId) : null;
                  if (!parcelId || !Number.isFinite(parcelId)) {
                    navigate("/bookings", { replace: true });
                    return;
                  }

                  const code = slip?.currencyType ?? slip?.currency ?? "GBP";
                  const currency =
                    typeof code === "string" && code.trim()
                      ? code.trim().toLowerCase()
                      : "gbp";

                  // Make sure Booking → Payment guard allows retry flow
                  sessionStorage.setItem("bookingApisCompleted", "1");

                  // Force a NEW intent now (per your requirement).
                  const data = await createStripePaymentIntent(parcelId, {
                    currency,
                  });
                  persistStripePaymentRecordIdFromIntentResponse(data);

                  const cs = data?.clientSecret;
                  const pk = data?.publishableKey;
                  if (cs && pk) {
                    sessionStorage.setItem("stripeRetryParcelId", String(parcelId));
                    sessionStorage.setItem("stripeRetryClientSecret", String(cs));
                    sessionStorage.setItem("stripeRetryPublishableKey", String(pk));
                  }

                  navigate("/bookings", { replace: true, state: { resumePayment: true } });
                } catch {
                  navigate("/bookings", { replace: true, state: { resumePayment: true } });
                } finally {
                  setRetrying(false);
                }
              }}
            >
              {retrying ? "Restarting..." : "Try again"}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => {
                sessionStorage.removeItem("bookingApisCompleted");
                navigate("/", { replace: true });
              }}
            >
              Back to home & pay later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
